import { MenuItem, TextField } from '@mui/material'
import React from 'react'

import {
	getPurchaseOrderOptions,
	getWorkOrderOptions,
	type BillingLogEntryResponse,
} from '../../types/Contracts/BillingLog'

type Props = {
	fieldKey: 'purchaseOrder' | 'workOrder'
	label: string
	value: string
	/** The purchase order picked on the form; the work orders offered are the ones logged under it. */
	purchaseOrder: string
	logs: BillingLogEntryResponse[]
	onChange: (value: string) => void
	required: boolean
	disabled?: boolean
	error?: string
	helperText?: string
}

/** Purchase Order or Work Order select, filled from the project's Contract Management log. */
export function BillingOrderSelect({
	fieldKey,
	label,
	value,
	purchaseOrder,
	logs,
	onChange,
	required,
	disabled = false,
	error,
	helperText,
}: Props) {
	const isWorkOrder = fieldKey === 'workOrder'
	const options = React.useMemo(
		() => (isWorkOrder ? getWorkOrderOptions(logs, purchaseOrder) : getPurchaseOrderOptions(logs)),
		[isWorkOrder, logs, purchaseOrder],
	)
	const notInLog = !!value && !options.includes(value)

	let hint = helperText
	if (notInLog) hint = 'This value is no longer in Contract Management.'
	else if (!isWorkOrder && options.length === 0) hint = 'Add entries in Contract Management.'
	else if (isWorkOrder && !purchaseOrder.trim()) hint = 'Select a purchase order first.'
	else if (isWorkOrder && options.length === 0) hint = 'This purchase order has no work orders.'

	return (
		<TextField
			select
			label={label}
			size='small'
			fullWidth
			required={required}
			value={value}
			disabled={disabled || (isWorkOrder && options.length === 0 && !notInLog)}
			error={!!error}
			helperText={error ?? hint}
			onChange={(e) => onChange(e.target.value)}
		>
			{isWorkOrder && !required && (
				<MenuItem value=''>
					<em>None</em>
				</MenuItem>
			)}
			{notInLog && (
				<MenuItem value={value} disabled>
					{value} (not in log)
				</MenuItem>
			)}
			{options.map((option) => (
				<MenuItem key={option} value={option}>
					{option}
				</MenuItem>
			))}
		</TextField>
	)
}
